import React, {useState} from "react";
import {Tag, styled} from "@reside/ui";

import {CommitWithResults} from "../types";
import {relativeTime} from "../utils";
import {CommitStatusBadge} from "./CommitStatusBadge";
import {CommitDetailPanel} from "./CommitDetailPanel";
import {Table, Th, Td, SectionTitle} from "./table-primitives";

const Tr = styled.tr<{isLatest?: boolean; isExpanded?: boolean}>`
  cursor: pointer;
  ${({isLatest}) => isLatest && "border-left: 3px solid #2bb7d9;"}
  ${({isExpanded}) => isExpanded && "background: #f5f6f7;"}

  &:hover {
    background: #fafcff;
  }
`;

const CommitMessage = styled.span`
  display: inline-block;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CommitHash = styled.code`
  color: #596973;
  font-size: 12px;
  margin-right: 6px;
`;

const ExpandIcon = styled.span<{expanded: boolean}>`
  display: inline-block;
  transition: transform 0.2s;
  transform: ${({expanded}) => (expanded ? "rotate(90deg)" : "rotate(0)")};
  margin-right: 8px;
  font-size: 10px;
  color: #596973;
`;

function countErrors(commit: CommitWithResults): number {
  return commit.template_results.reduce(
    (sum, r) => sum + (r.errors?.length ?? 0),
    0,
  );
}

const CommitRow = ({
  commit,
  isLatest,
  isExpanded,
  onToggle,
}: {
  commit: CommitWithResults;
  isLatest: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}): JSX.Element => {
  const errorCount = countErrors(commit);
  return (
    <React.Fragment>
      <Tr isLatest={isLatest} isExpanded={isExpanded} onClick={onToggle}>
        <Td>
          <ExpandIcon expanded={isExpanded}>&#9654;</ExpandIcon>
        </Td>
        <Td>
          <CommitStatusBadge commit={commit} />
        </Td>
        <Td>
          <CommitHash>{commit.process.hash.substring(0, 7)}</CommitHash>
          <CommitMessage>{commit.process.message}</CommitMessage>
        </Td>
        <Td>{commit.process.author}</Td>
        <Td>{relativeTime(commit.process.changed_date)}</Td>
        <Td>
          {commit.process.processed_template_count}/
          {commit.process.total_templates}
        </Td>
        <Td>
          {errorCount > 0 ? (
            <Tag color="deepOrange100">{errorCount}</Tag>
          ) : (
            "—"
          )}
        </Td>
      </Tr>
      {isExpanded && (
        <tr>
          <td colSpan={7}>
            <CommitDetailPanel commit={commit} />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

const CommitTableHead = (): JSX.Element => (
  <thead>
    <tr>
      <Th />
      <Th>Status</Th>
      <Th>Commit</Th>
      <Th>Author</Th>
      <Th>Date</Th>
      <Th>Progress</Th>
      <Th>Errors</Th>
    </tr>
  </thead>
);

export const CommitHistoryTable = ({
  commits,
}: {
  commits: CommitWithResults[];
}): JSX.Element => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleRow = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <>
      <SectionTitle>Commit History</SectionTitle>
      <Table>
        <CommitTableHead />
        <tbody>
          {commits.map((commit, index) => (
            <CommitRow
              key={commit.process.id}
              commit={commit}
              isLatest={index === 0}
              isExpanded={expandedId === commit.process.id}
              onToggle={() => toggleRow(commit.process.id)}
            />
          ))}
          {commits.length === 0 && (
            <tr>
              <Td colSpan={7} style={{textAlign: "center", color: "#596973"}}>
                No commits found.
              </Td>
            </tr>
          )}
        </tbody>
      </Table>
    </>
  );
};
